import { LocationStrategy, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeHi from '@angular/common/locales/hi';
import localeKn from '@angular/common/locales/kn';
import localeMl from '@angular/common/locales/ml';
import localeTa from '@angular/common/locales/ta';
import localeTe from '@angular/common/locales/te';
import { LOCALE_ID, NgModule, isDevMode, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NavigationError, Router } from '@angular/router';
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker';
import { TranslateModule } from '@ngx-translate/core';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthGuard } from './authentication/_guards/auth.guard';
import { AuthInterceptor } from './interceptor/auth.interceptor.service';
import { CentralizedErrorInterceptor } from './interceptor/centralized-error.interceptor';
import { CustomToastrModule } from './shared/components/custom-toastr-module/custom-toastr.module';
import { LoadingScreenComponent } from './shared/components/loading-screen/loading-screen.component';
import { LoadingScreenService } from './shared/components/loading-screen/loading-screen.service';
import { TranslationService } from './shared/services/translation.service';
import { SharedModule } from './shared/shared.module';

// English ('en') is Angular's built-in default locale data, needs no registration. The five other
// seeded languages (docs/release-scripts-LANGUAGES_20260819.js, TDD 13 §7.3/§12) do - Angular's
// DatePipe/CurrencyPipe/etc. throw at runtime for an unregistered locale rather than silently falling
// back to English. Previously registered 'fr'/'ja' here instead, leftover from the angular-base
// template this app was copied from - neither is a language RMBF actually seeds or offers.
registerLocaleData(localeHi);
registerLocaleData(localeTa);
registerLocaleData(localeTe);
registerLocaleData(localeKn);
registerLocaleData(localeMl);
@NgModule({
  declarations: [AppComponent, LoadingScreenComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    CustomToastrModule,

    TranslateModule.forRoot(),

    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [
    LoadingScreenService,
    AuthGuard,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CentralizedErrorInterceptor,
      multi: true
    },
    provideAnimationsAsync(),
    provideCharts(withDefaultRegisterables()),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { autoFocus: 'dialog', maxWidth: '95vw' }
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => {
        const lang = navigator.language || 'en-US';
        return lang === 'en-US' ? 'en-IN' : lang;
      }
    },
    {
      provide: LOCALE_ID,
      useFactory: (translationService: TranslationService) => translationService.currentLang,
      deps: [TranslationService]
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  private updates = inject(SwUpdate);

  constructor() {
    const router = inject(Router);
    const locationStrategy = inject(LocationStrategy);

    router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        const chunkFailedMessage = /Loading chunk [\d]+ failed|Loading failed for the module|Failed to fetch dynamically imported module/;
        if (chunkFailedMessage.test(event.error?.message || '')) {
          // A deploy removed the chunks this tab was built against. Load the new build once, straight onto the
          // page the user was navigating to; if it fails again right after, stop instead of reloading in a loop.
          const RELOAD_KEY = 'chunkReloadAt';
          const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
          if (Date.now() - lastReload < 10000) {
            console.error('Chunk load failed again right after a reload. Not reloading.', event.error);
            return;
          }
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          console.warn('Chunk load failed. Forcing reload...');
          const activated = this.updates.isEnabled ? this.updates.activateUpdate().catch(() => false) : Promise.resolve(false);
          activated.then(() => document.location.assign(locationStrategy.prepareExternalUrl(event.url)));
        }
      }
    });
  }
}
